import {
  newDecision,
  type DecisionDraft,
  type DecisionRecord,
} from '../decisions/decision-record.js';
import type { DeloadAssessment } from './deload.js';
import type { PriorityChange } from './priorities.js';
import type { SubstitutionCandidate } from './substitution.js';
import type { ReorganizeDecision } from './week-reorganization.js';
import type { StallAssessment } from '../load/stall.js';
import type { PhaseDecision } from './block-phase.js';
import type { VolumeAdjustment } from './volume-response.js';

/**
 * Adaptadores de cada motor de la Fase 3 a la forma canonica de decision.
 *
 * Existen para que el panel de propuestas tenga UNA lista que pintar en lugar
 * de cuatro casos especiales, y para que el registro de decisiones se llene
 * solo. Cada uno devuelve `null` cuando no hay nada que decidir: una propuesta
 * de "no cambies nada" ocuparia sitio en la pantalla sin pedir nada al usuario,
 * y a la tercera se dejan de leer.
 */

/**
 * Confianza por fuente. CONVENCION declarada: mide lo firme que es la señal,
 * no lo acertada que sea la decision.
 */
export const PROPOSAL_CONFIDENCE = {
  /** Una regla de §4.3 se cumplio entera. */
  volumeRule: 0.8,
  /** La prioridad la declara el usuario: no hay incertidumbre de medida. */
  priority: 1,
  /**
   * Que una sesion se ha perdido es un HECHO, no una estimacion. Lo que no es
   * seguro es que el dia elegido sea el mejor: el motor no sabe si el sabado el
   * usuario puede ir al gimnasio. De ahi que no llegue a 1.
   */
  reorganization: 0.9,
  /**
   * Tres sesiones sin progresar es una señal firme, pero la CAUSA no lo es: el
   * mismo dato encaja con falta de recuperacion, de energia o de tecnica. Se
   * propone la respuesta de la escalera, no un diagnostico.
   */
  stall: 0.75,
  /**
   * El arbol del §2.3 es determinista, asi que la DECISION es firme dado el
   * dato. Lo que no siempre es firme es el dato: sin cintura ni grasa el arbol
   * responde «mantenimiento por falta de señal», y eso es una eleccion segura,
   * no un diagnostico. La confianza real la lleva `band` en las entradas.
   */
  phase: 0.85,
} as const;

export function proposalFromVolume(
  adjustment: VolumeAdjustment,
  decidedAt: string,
): DecisionRecord | null {
  if (adjustment.deltaSets === 0) return null;

  const draft: DecisionDraft = {
    decisionType: 'volume_change',
    inputs: {
      muscle: adjustment.muscle,
      currentSets: adjustment.currentSets,
      clampedBySafety: adjustment.clampedBySafety,
    },
    previousValue: adjustment.currentSets,
    newValue: adjustment.proposedSets,
    source: 'algorithm',
    reasonCodes: adjustment.reasonCodes,
    reasonText: null,
    confidence: PROPOSAL_CONFIDENCE.volumeRule,
    classification: adjustment.change,
  };

  return newDecision(draft, decidedAt);
}

export function proposalFromDeload(
  assessment: DeloadAssessment,
  decidedAt: string,
): DecisionRecord | null {
  // `none` no propone nada. `halted` tampoco: una bandera roja detiene las
  // recomendaciones, y colarla como propuesta seria darle al usuario algo que
  // aprobar cuando lo que toca es que no se le proponga nada.
  if (assessment.action === 'none' || assessment.action === 'halted') return null;
  if (assessment.change === null) return null;

  const draft: DecisionDraft = {
    decisionType: 'deload',
    inputs: {
      score: assessment.score,
      signals: assessment.signals.map((señal) => señal.code),
    },
    previousValue: null,
    newValue: assessment.action,
    source: 'algorithm',
    reasonCodes: assessment.signals.map((señal) => señal.code),
    reasonText: null,
    // La propia puntuacion es la medida de lo firme que es la señal.
    confidence: assessment.score / 100,
    classification: assessment.change,
  };

  return newDecision(draft, decidedAt);
}

export function proposalFromPriorities(
  change: PriorityChange,
  decidedAt: string,
): DecisionRecord | null {
  if (change.classification === null) return null;

  const draft: DecisionDraft = {
    decisionType: 'priority_change',
    inputs: { changes: change.changes.length },
    previousValue: Object.fromEntries(change.changes.map((c) => [c.muscle, c.from])),
    newValue: Object.fromEntries(change.changes.map((c) => [c.muscle, c.to])),
    // La pide el usuario; el algoritmo solo la registra y la clasifica.
    source: 'user',
    reasonCodes: change.changes.map((c) => `${c.muscle}:${c.from}->${c.to}`),
    reasonText: null,
    confidence: PROPOSAL_CONFIDENCE.priority,
    classification: change.classification,
  };

  return newDecision(draft, decidedAt);
}

export function proposalFromSubstitution(
  originalSlug: string,
  candidate: SubstitutionCandidate,
  decidedAt: string,
): DecisionRecord {
  const draft: DecisionDraft = {
    decisionType: 'exercise_substitution',
    inputs: { originalSlug, score: candidate.score },
    previousValue: originalSlug,
    newValue: candidate.slug,
    source: 'user',
    reasonCodes: candidate.reasons,
    reasonText: candidate.reason,
    // La similitud ES la confianza: cuanto mas se parece, menos cambia el estimulo.
    confidence: candidate.score,
    classification: candidate.change,
  };

  return newDecision(draft, decidedAt);
}

export function proposalFromReorganization(
  decision: ReorganizeDecision,
  decidedAt: string,
): DecisionRecord | null {
  // Sin sesiones perdidas no hay nada que decidir, aunque el motor haya
  // recolocado un cardio: mover una carrera un dia no merece una tarjeta.
  if (decision.missedSessionIds.length === 0) return null;

  const draft: DecisionDraft = {
    decisionType: 'week_reorganization',
    inputs: {
      /**
       * Los movimientos con su `sessionId`. Sin esto la propuesta se puede
       * pintar pero no APLICAR: «del lunes al sabado» no dice que sesion.
       */
      moves: decision.actions
        .filter((accion) => accion.kind === 'reschedule')
        .map((accion) => ({
          sessionId: accion.sessionId,
          from: accion.from,
          to: accion.to,
        })),
      missedSessions: decision.missedSessionIds.length,
      volumeRatio: Number(decision.volumeRatio.toFixed(3)),
      belowVolumeFloor: decision.belowVolumeFloor,
      suggestsStructuralReview: decision.suggestsStructuralReview,
    },
    previousValue: decision.actions.map((accion) =>
      accion.kind === 'reschedule' ? accion.from : null,
    ),
    newValue: decision.actions.map((accion) =>
      accion.kind === 'reschedule' ? accion.to : null,
    ),
    source: 'algorithm',
    reasonCodes: decision.reasonCodes,
    reasonText: null,
    confidence: PROPOSAL_CONFIDENCE.reorganization,
    classification: decision.change,
  };

  return newDecision(draft, decidedAt);
}

/**
 * Los cuatro escalones del §9 se registran con el MISMO `decisionType`.
 *
 * Repartirlos entre `load_adjust` y `exercise_substitution` haria el historial
 * ilegible —habria que cruzar tres tipos para saber cuantas veces respondio el
 * sistema a un estancamiento— y la revision estructural no encaja en ninguno de
 * los que ya existian. Ver la migracion 0011.
 */
export function proposalFromStall(
  exercise: { slug: string; equipmentId: string | null },
  assessment: StallAssessment,
  decidedAt: string,
): DecisionRecord | null {
  // El escalon 1 es ruido declarado: no se propone nada, y `change` es null.
  if (!assessment.stalled || assessment.change === null) return null;

  const draft: DecisionDraft = {
    decisionType: 'stall_response',
    inputs: {
      exerciseSlug: exercise.slug,
      // Hacen falta para APLICAR: el estado de progresion se identifica por
      // ejercicio Y equipamiento (ADR-014), y la accion dice que hacer con el.
      equipmentId: exercise.equipmentId,
      action: assessment.action,
      stallCounter: assessment.stallCounter,
      loadFactor: assessment.loadFactor,
      rebuildSessions: assessment.rebuildSessions,
    },
    previousValue: null,
    newValue: assessment.nextStrategy ?? assessment.action,
    source: 'algorithm',
    reasonCodes: assessment.reasonCodes,
    reasonText: null,
    confidence: PROPOSAL_CONFIDENCE.stall,
    classification: assessment.change,
  };

  return newDecision(draft, decidedAt);
}

/**
 * Cambio de fase al cerrar un bloque (§2.2, §2.3).
 *
 * Devuelve `null` cuando la fase NO cambia: encadenar otro bloque de la misma
 * fase no es una decision que haya que aprobar, es continuar. El §10 solo exige
 * permiso para cambiar de fase.
 */
export function proposalFromPhase(
  decision: PhaseDecision,
  decidedAt: string,
): DecisionRecord | null {
  if (decision.change === null) return null;

  const draft: DecisionDraft = {
    decisionType: 'phase_change',
    inputs: {
      phase: decision.phase,
      plannedWeeks: decision.plannedWeeks,
      // `band` —la banda de adiposidad— se fue el 21/09/2026 con el arbol que
      // la calculaba. Lo que explica la decision ahora son los `reasonCodes`,
      // que ya viajan debajo y distinguen lo que hay que distinguir: no es lo
      // mismo «lo pediste tu» que «aun no se lo que pediste».
    },
    previousValue: null,
    newValue: decision.phase,
    source: 'algorithm',
    reasonCodes: decision.reasonCodes,
    reasonText: null,
    confidence: PROPOSAL_CONFIDENCE.phase,
    classification: decision.change,
  };

  return newDecision(draft, decidedAt);
}

/** Quita los nulos de una tanda de propuestas. */
export function collectProposals(
  proposals: readonly (DecisionRecord | null)[],
): DecisionRecord[] {
  return proposals.filter((propuesta): propuesta is DecisionRecord => propuesta !== null);
}
